import { LinkedContextState } from '@context/LinkedItemZoneContext'
import { API } from '@utils/queryBuilder'
import { useAppStore } from '@store'
import { LinkedItemsID } from '@types'
import getStandardMaterial, { BackAcabadosInfo } from '@utils/conversion/getStandardMaterial'

type GlbCache = {
  clear: () => void
}

type WindowWithGlbCache = Window & {
  glbCache?: GlbCache
}

export default async function handleGroupClick(
  idAcabadoUnidadModeloAcabado: number,
  context: LinkedContextState,
  acabado: string,
) {
  const textures = await API().api.acabadosGetTexturas(idAcabadoUnidadModeloAcabado)

  const isEncimeraChild =
    context.highlighted?.startsWith('linkedSon_') &&
    useAppStore.getState().items.get(context.highlighted)?.description === 'Encimera'

  const currentSelectedZoneDisplayName = (context.selectedTexture as any)?.zoneDisplayName
  const rightPanelZonaName = isEncimeraChild
    ? 'Encimera'
    : currentSelectedZoneDisplayName || context.rightInfo?.name || ''

  context.setRightInfo({ name: rightPanelZonaName, textures: textures.data })
  context.setAcabado(acabado)

  if (!isEncimeraChild) {
    const currentObject = context.object?.current
    if (currentObject && currentObject.textures) {
      const highlightedZoneName = context.highlighted?.split('!')?.[1] || ''

      const highlightedTexture = currentObject.textures.find(
        (t: any) => t.zoneName === highlightedZoneName,
      )
      const highlightedZoneDisplayName =
        highlightedTexture?.zoneDisplayName || currentSelectedZoneDisplayName

      const preferredZoneName = highlightedZoneName || (context.selectedTexture as any)?.zoneName
      const preferredZoneDisplayName = highlightedZoneDisplayName || currentSelectedZoneDisplayName

      // console.log('[handleGroupClick] finding-matching-texture', {
      //   highlighted: context.highlighted,
      //   highlightedZoneName,
      //   highlightedZoneDisplayName,
      //   acabadoToFind: acabado,
      //   preferredZoneName,
      //   preferredZoneDisplayName,
      //   rightPanelZonaName,
      //   availableTextures: currentObject.textures.map((t: any) => ({
      //     zoneName: t.zoneName,
      //     zoneDisplayName: t.zoneDisplayName,
      //     acabado: t.acabado,
      //     modelo: t.modelo,
      //   })),
      // })

      let matchingTexture = currentObject.textures.find(
        (texture: any) =>
          texture.modelo !== null &&
          texture.acabado !== null &&
          texture.acabado === acabado &&
          preferredZoneName &&
          texture.zoneName === preferredZoneName,
      )

      if (!matchingTexture && preferredZoneDisplayName) {
        matchingTexture = currentObject.textures.find(
          (texture: any) =>
            texture.modelo !== null &&
            texture.acabado !== null &&
            texture.acabado === acabado &&
            texture.zoneDisplayName === preferredZoneDisplayName,
        )
      }

      if (matchingTexture) {
        const isCorrectZone =
          (!preferredZoneName || matchingTexture.zoneName === preferredZoneName) &&
          (!preferredZoneDisplayName ||
            matchingTexture.zoneDisplayName === preferredZoneDisplayName)

        if (isCorrectZone) {
          context.setSelectedTexture(matchingTexture)
        }
      }
    }
  }

  const store = useAppStore.getState()
  const modeEditor = context.modeEditor
  const centerInfo = context.centerInfo

  try {
    if (modeEditor === 'All') {
      const linkedItemIds = Array.from(store.items.keys()).filter((id) =>
        id.startsWith('linkedItem_'),
      )

      if (modeEditor === 'All') {
        const selectedAcabadoInfo = centerInfo.find((c) => c.acabado === acabado)

        if (selectedAcabadoInfo) {
          const acabadoTextures = await API().api.acabadosGetTexturas(
            selectedAcabadoInfo.idAcabadoUnidadModeloAcabado,
          )
          const selectedTexture = acabadoTextures.data.find(
            (t) => t.descripcion?.includes(acabado) || t.nombre?.includes(acabado),
          )

          if (selectedTexture) {
            const editLinkedTexture2 = store.editLinkedTexture2
            const firstLinkedItemId = linkedItemIds[0]
            if (firstLinkedItemId) {
              const material = getStandardMaterial(
                selectedTexture as BackAcabadosInfo,
                acabado,
                'AcabadoModelo',
              )

              const firstItem = store.items.get(firstLinkedItemId)
              const doorZone = Array.isArray(firstItem?.textures)
                ? firstItem.textures.find((t) => t.zoneDisplayName === 'Puerta')
                : null
              const zoneName = doorZone?.zoneName || 'Puerta'

              const newTexture = editLinkedTexture2(
                firstLinkedItemId as LinkedItemsID,
                zoneName,
                material,
                'All',
                new Set(linkedItemIds as LinkedItemsID[]),
              )

              if (newTexture) {
                useAppStore.setState((s) => ({ ...s, shouldUpdateCamera: true }))
                const glbCache = (window as WindowWithGlbCache).glbCache
                if (glbCache && glbCache.clear) {
                  glbCache.clear()
                }
                setTimeout(() => {
                  useAppStore.setState((s) => ({ ...s, shouldUpdateCamera: true }))
                }, 100)

                try {
                  const complemento = (material as any)?.complemento as string | undefined
                  if (context.rightInfo?.name?.toLowerCase() === 'zocalo' && complemento) {
                    const highlightedId = context.highlighted?.split('!')?.[0]
                    const highlightedItem = useAppStore.getState().items.get(highlightedId as any)
                    const zocaloTex = highlightedItem?.textures?.find(
                      (t: any) => t.zoneDisplayName === 'Zocalo',
                    )
                    const linkedId = zocaloTex?.linkedTextureID
                    if (typeof linkedId === 'number') {
                      const info = await API().api.acabadosGetAcabadoZonaModeloAcabadoInfo(linkedId)
                      const data = (info as any)?.data ?? info
                      const acabados = Array.isArray(data)
                        ? data.find((z: any) => (z?.zona || '').toLowerCase().includes('zocalo'))
                            ?.acabados ||
                          data[0]?.acabados ||
                          []
                        : []
                      const idAcabado =
                        acabados[0]?.idAcabadoUnidad || acabados[0]?.idAcabadoUnidadModeloAcabado
                      if (idAcabado) {
                        const texResp = await API().api.acabadosGetTexturas(idAcabado)
                        const list = (texResp as any)?.data ?? texResp
                        const cmp = complemento.toLowerCase()
                        const chosen = (Array.isArray(list) ? list : []).find((t: any) => {
                          const n = (t?.nombre || '').toLowerCase()
                          const d = (t?.descripcion || '').toLowerCase()
                          return n === cmp || n.includes(cmp) || d.includes(cmp)
                        })
                        if (chosen) {
                          context.setRightInfo({ name: 'Zocalo', textures: list })
                          context.setSelectedTexture({
                            ...(chosen as any),
                            zoneName: zocaloTex.zoneName,
                            zoneDisplayName: 'Zocalo',
                          })
                        }
                      }
                    }
                  }
                } catch (e) {
                  // nooop
                }
              }
            }
          }
        }
      }
    }
  } catch (error) {
    console.error('Error al actualizar contexto:', error)
  }

  return
}
